import React, { useEffect, useState } from 'react';
import CommonModal from '@/components/common/CommonModal';
import CommonTable, { TableColumn } from '@/components/common/CommonTable';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateTime, formatRand, GetFilterUrl } from '@/utility/helper';
import { Calendar, CreditCard, Hash, Eye, DollarSign } from 'lucide-react';
import Pagination from '@/components/common/Pagination';
import { IPageInfo } from '@/interfaces/common.interface';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ICashSettlementOrdersResponse, ICashSettlementOrderTransaction, ISummary } from '@/interfaces/CashSettlement/cashSettlement.interface';
import { useRoleNavigate } from '@/hooks/useRoleNavigate';
import { RoutesLink } from '@/utility/RouteLinks';
import { IOrder } from '@/interfaces/Order/order.interface';

interface SettlementOrdersModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    driverId?: string | null;
    cashSettlementStatus?: 'pending' | 'collected' | 'settled' | null;
}

export const SettlementOrdersModal: React.FC<SettlementOrdersModalProps> = ({
    open,
    onOpenChange,
    driverId,
    cashSettlementStatus
}) => {
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [summary, setSummary] = useState<ISummary>({ totalOrders: 0, totalCashAmount: 0, totalCollectedEarning: 0, totalSettledEarning: 0 });
    const [loading, setLoading] = useState(false);
    const [pageInfo, setPageInfo] = useState<IPageInfo>({ page: 1, limit: 10, total: 0, totalPages: 0 });
    const navigate = useRoleNavigate();

    const handleViewOrder = (orderId: string) => {
        navigate(RoutesLink.ViewOrders.replace(':id', orderId));
        onOpenChange(false);
    };

    const fetchOrders = async (page = 1) => {
        if (!driverId) return;
        setLoading(true);
        try {
            const urlParams = GetFilterUrl({ page: page, limit: pageInfo.limit, driverId: driverId, cashSettlementStatus: cashSettlementStatus });

            const response = await callApi<ICashSettlementOrdersResponse>(
                `${API_URL}${API_ENDPOINT.cashSettlement}/driver-orders?${urlParams}`,
                { method: 'GET' }
            );
            // Handling both potential wrappers
            const list = response.data?.collectedCash?.orders || [];
            const newSummary = response.data?.collectedCash?.summary || { totalOrders: 0, totalCashAmount: 0, totalCollectedEarning: 0, totalSettledEarning: 0 };
            setOrders(list);
            setSummary(newSummary);
            if (response.data?.pageInfo) {
                setPageInfo(response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 });
            }
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        console.log(driverId)
        if (open && driverId) {
            fetchOrders(1);
        } else {
            setOrders([]);
            setPageInfo({ page: 1, limit: 10, total: 0, totalPages: 0 });
        }
    }, [open, driverId]);

    const columns: TableColumn<any>[] = [
        {
            key: "orderId",
            header: "Order / Ref ID",
            render: (item) => {
                const order = item.order || item;
                return (
                    <div
                        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleViewOrder(order?.id)}
                    >
                        <Hash className="w-3 h-3 text-muted-foreground" />
                        <span className="font-mono text-sm font-semibold text-primary underline underline-offset-2">{order.orderId}</span>
                    </div>
                )
            }
        },
        {
            key: "date",
            header: "Date",
            render: (item) => {
                const order = item.order || item;
                return (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDateTime(order.createdAt || item.createdAt)}
                    </div>
                )
            }
        },
        {
            key: "orderAmount",
            header: "Order Amount",
            render: (item) => {
                const order = item.order || item;
                return (
                    <div className="font-bold text-foreground">
                        {formatRand(Number(order.finalAmount || 0))}
                    </div>
                )
            }
        },
        {
            key: "earning",
            header: "Driver Earning",
            render: (item) => {
                const order = item.order || item;
                return (
                    <div className="font-medium text-orange-600">
                        {formatRand(Number(order.driverEarning?.earning || 0))}
                    </div>
                )
            }
        },
        {
            key: "status",
            header: "Status",
            render: (item) => {
                const order = item.order || item;
                return (
                    <StatusBadge status={item.cashSettlementStatus || order.status} />
                )
            }
        }
    ];

    const actions = [
        {
            text: 'View Order',
            icon: <Eye size={16} strokeWidth={1.5} className="cursor-pointer text-muted-foreground hover:text-primary transition-colors" />,
            onClick: (record: any) => {
                const order = record.order || record;
                handleViewOrder(order?.id);
            },
        }
    ];

    return (
        <CommonModal
            open={open}
            onOpenChange={onOpenChange}
            title="Order History"
            size="xl"
        >
            <div className="space-y-4 pt-4">
                <CommonTable
                    columns={columns}
                    data={orders}
                    loading={loading}
                    emptyMessage="No orders found."
                    actions={actions}
                />

                {pageInfo.totalPages > 1 && (
                    <Pagination
                        currentPage={pageInfo.page}
                        totalPages={pageInfo.totalPages}
                        onPageChange={fetchOrders}
                    />
                )}

                <div className="mt-6 pt-4 border-t border-dashed flex justify-between items-center p-4 bg-secondary/10 rounded-xl">
                    <div className="flex gap-4">
                        <span className="font-semibold text-muted-foreground">Total Orders: <span className="text-foreground">{summary.totalOrders}</span></span>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                        <div className="flex items-center gap-4">
                            <span className="font-medium text-sm text-muted-foreground">Driver Earning:</span>
                            <div className="flex items-center gap-1.5 text-orange-600">
                                <span className="text-lg font-bold">{formatRand(cashSettlementStatus === "collected" ? summary?.totalCollectedEarning : summary?.totalSettledEarning)}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-muted-foreground">Total Cash Collected:</span>
                            <div className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-green-600" />
                                <span className="text-2xl font-black text-foreground">{formatRand(summary.totalCashAmount)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CommonModal>
    );
};
